<div class="benchmark-dashboard">
  <!-- Loading Indicator -->
  <div *ngIf="isLoading" class="loading-container">
    <div class="loading-spinner"></div>
    <p class="loading-text">Generating benchmark report...</p>
  </div>

  <!-- Sector Overview -->
  <div class="sector-overview-section" *ngIf="!isLoading">
    <!-- Dashboard Header Summary -->
    <div class="dashboard-summary-header tw-hidden @md:tw-flex">
      <div class="summary-header-content">
        <div class="summary-primary">
          <h2 class="summary-title">Workforce Benchmark Analysis</h2>
          <div class="summary-meta">
            <div class="meta-item">
              <span class="meta-icon">📅</span>
              <span class="meta-label">Time Period:</span>
              <span class="meta-value"
                >{{ dateFrom | date: 'MMM yyyy' }} - {{ dateTo | date: 'MMM yyyy' }}</span
              >
              <span class="meta-badge">{{ dateRangeLabel }}</span>
            </div>
            <div class="meta-item" *ngIf="isJobFunctionFilterActive">
              <span class="meta-icon">💼</span>
              <span class="meta-label">Functions:</span>
              <span class="meta-value filter-active">{{ selectedJobFunctions?.join(', ') }}</span>
            </div>
            <div class="meta-item" *ngIf="selectedLevels && selectedLevels.length > 0">
              <span class="meta-icon">📊</span>
              <span class="meta-label">Levels:</span>
              <span class="meta-value filter-active">{{ selectedLevels.join(', ') }}</span>
            </div>
            <div class="meta-item" *ngIf="selectedRoles && selectedRoles.length > 0">
              <span class="meta-icon">👤</span>
              <span class="meta-label">Roles:</span>
              <span class="meta-value filter-active">{{ selectedRoles.join(', ') }}</span>
            </div>
            <div
              class="meta-item"
              *ngIf="!isJobFunctionFilterActive && (!selectedLevels || selectedLevels.length === 0) && (!selectedRoles || selectedRoles.length === 0)">
              <span class="meta-icon">✓</span>
              <span class="meta-label">Scope:</span>
              <span class="meta-value">All Functions & Levels</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="summary-stats-grid">
      <div class="summary-stat-card">
        <p class="summary-stat-label">Total Sample*</p>
        <p class="summary-stat-value">{{ totalHeadcount | number }}</p>
      </div>
      <div class="summary-stat-card positive">
        <p class="summary-stat-label">New Hires ({{ dateRangeLabel }})</p>
        <p class="summary-stat-value growth">{{ totalArrivals | number }}</p>
      </div>
      <div class="summary-stat-card negative">
        <p class="summary-stat-label">Departures ({{ dateRangeLabel }})</p>
        <p class="summary-stat-value decline">{{ totalDepartures | number }}</p>
      </div>
      <div class="summary-stat-card">
        <p class="summary-stat-label">Avg Growth</p>
        <p class="summary-stat-value">{{ sectorAvg.growthRate }}%</p>
      </div>
      <div class="summary-stat-card">
        <p class="summary-stat-label">Avg Turnover</p>
        <p class="summary-stat-value">{{ sectorAvg.turnoverRate }}%</p>
      </div>
      <div class="summary-stat-card">
        <p class="summary-stat-label">Avg Departed Tenure</p>
        <p class="summary-stat-value">
          {{ sectorAvg.departedTenure }}<span class="unit"> yrs</span>
        </p>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="charts-grid-2col">
      <!-- Health Score Chart -->
      <div class="chart-card">
        <highcharts-chart
          *ngIf="!isLoading && companies.length > 0"
          [Highcharts]="Highcharts"
          [options]="healthScoreChartOptions"
          [(update)]="healthScoreUpdateFlag"
          style="width: 100%; display: block;">
        </highcharts-chart>
      </div>

      <!-- Growth vs Turnover Scatter -->
      <div class="chart-card">
        <highcharts-chart
          *ngIf="!isLoading && companies.length > 0"
          [Highcharts]="Highcharts"
          [options]="scatterChartOptions"
          [(update)]="scatterUpdateFlag"
          style="width: 100%; display: block;">
        </highcharts-chart>
      </div>
    </div>
  </div>

  <!-- Legend -->
  <div class="legend-section" *ngIf="!isLoading">
    <span class="legend-label tw-hidden @md:tw-flex">Signal Colors:</span>
    <div class="legend-item">
      <div class="legend-color healthy"></div>
      <span class="legend-text">Healthy / Above Avg</span>
    </div>
    <div class="legend-item">
      <div class="legend-color watch"></div>
      <span class="legend-text">Watch</span>
    </div>
    <div class="legend-item">
      <div class="legend-color concern"></div>
      <span class="legend-text">Concern / Below Avg</span>
    </div>
  </div>

  <!-- Main Table -->
  <div class="table-container" *ngIf="!isLoading">
    <table class="benchmark-table">
      <thead>
        <!-- Section Headers Row -->
        <tr class="section-headers">
          <th colspan="2"></th>
          <th
            [attr.colspan]="isJobFunctionFilterActive ? 2 : 5"
            class="section-header point-in-time">
            AS OF {{ dateTo | date: 'MMM yyyy' }}
          </th>
          <th colspan="4" class="section-header over-time">
            {{ dateFrom | date: 'MMM yyyy' }} - {{ dateTo | date: 'MMM yyyy' }} ({{ dateRangeLabel







            }})
          </th>
        </tr>
        <!-- Column Headers Row -->
        <tr>
          <th (click)="handleSort('healthScore')" [class.active]="sortBy === 'healthScore'">
            HEALTH {{ sortBy === 'healthScore' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th (click)="handleSort('company')" [class.active]="sortBy === 'company'">
            COMPANY {{ sortBy === 'company' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th (click)="handleSort('headcount')" [class.active]="sortBy === 'headcount'">
            SAMPLE* {{ sortBy === 'headcount' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th
            *ngIf="!isJobFunctionFilterActive"
            (click)="handleSort('engPct')"
            [class.active]="sortBy === 'engPct'">
            ENG % {{ sortBy === 'engPct' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th
            *ngIf="!isJobFunctionFilterActive"
            (click)="handleSort('salesPct')"
            [class.active]="sortBy === 'salesPct'">
            SALES % {{ sortBy === 'salesPct' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th
            *ngIf="!isJobFunctionFilterActive"
            (click)="handleSort('engToSalesRatio')"
            [class.active]="sortBy === 'engToSalesRatio'">
            ENG:SALES {{ sortBy === 'engToSalesRatio' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th (click)="handleSort('activeTenure')" [class.active]="sortBy === 'activeTenure'">
            AVG TENURE {{ sortBy === 'activeTenure' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th
            (click)="handleSort('growthRate')"
            [class.active]="sortBy === 'growthRate'"
            class="metric-with-trend">
            GROWTH {{ sortBy === 'growthRate' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th
            (click)="handleSort('turnoverRate')"
            [class.active]="sortBy === 'turnoverRate'"
            class="metric-with-trend">
            TURNOVER {{ sortBy === 'turnoverRate' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th
            (click)="handleSort('execTurnoverRate')"
            [class.active]="sortBy === 'execTurnoverRate'">
            EXEC TURN {{ sortBy === 'execTurnoverRate' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
          <th (click)="handleSort('departedTenure')" [class.active]="sortBy === 'departedTenure'">
            DEPARTED TENURE
            {{ sortBy === 'departedTenure' ? (sortDir === 'desc' ? '↓' : '↑') : '' }}
          </th>
        </tr>
      </thead>
      <tbody>
        <!-- Sector Average Row -->
        <tr class="sector-row">
          <td>
            <div class="health-badge sector-badge">
              {{ sectorAvg.healthScore }}
            </div>
          </td>
          <td class="company-name sector-name">📊 Average</td>
          <td class="metric-value">{{ sectorAvg.headcount | number }}</td>
          <td *ngIf="!isJobFunctionFilterActive" class="metric-value">{{ sectorAvg.engPct }}%</td>
          <td *ngIf="!isJobFunctionFilterActive" class="metric-value">{{ sectorAvg.salesPct }}%</td>
          <td *ngIf="!isJobFunctionFilterActive" class="metric-value">
            {{ sectorAvg.engToSalesRatio }}x
          </td>
          <td class="metric-value">{{ sectorAvg.activeTenure }} yrs</td>
          <td class="metric-with-trend-cell">
            <div class="metric-trend-container">
              <span class="metric-value">{{ sectorAvg.growthRate }}%</span>
              <span class="trend-placeholder">—</span>
            </div>
          </td>
          <td class="metric-with-trend-cell">
            <div class="metric-trend-container">
              <span class="metric-value">{{ sectorAvg.turnoverRate }}%</span>
              <span class="trend-placeholder">—</span>
            </div>
          </td>
          <td class="metric-value">{{ sectorAvg.execTurnoverRate }}%</td>
          <td class="metric-value">{{ sectorAvg.departedTenure }} yrs</td>
        </tr>

        <!-- Company Rows -->
        <ng-container *ngFor="let company of sortedCompanies; let i = index">
          <tr
            [class.selected]="selectedCompany === company.company"
            [class.even]="i % 2 === 0"
            (click)="onCompanySelect(selectedCompany === company.company ? null : company.company)">
            <td>
              <div
                class="health-badge"
                [style.background]="company.healthScoreBgColor"
                [style.border-color]="company.healthScoreColor"
                [style.color]="company.healthScoreColor">
                {{ company.healthScore }}
              </div>
            </td>
            <td class="company-name">
              {{ company.company }}
              <span *ngIf="selectedCompany === company.company" class="selected-indicator"
                >▼ Expanded</span
              >
            </td>
            <td class="metric-value">{{ company.headcount | number }}</td>
            <td *ngIf="!isJobFunctionFilterActive" class="metric-value">{{ company.engPct }}%</td>
            <td *ngIf="!isJobFunctionFilterActive" class="metric-value">{{ company.salesPct }}%</td>
            <td
              *ngIf="!isJobFunctionFilterActive"
              class="metric-value"
              [style.color]="company.engToSalesRatioColor">
              {{ company.engToSalesRatio }}x
            </td>
            <td class="metric-value" [style.color]="company.activeTenureColor">
              {{ company.activeTenure }} yrs
            </td>
            <td class="metric-with-trend-cell">
              <div class="metric-trend-container">
                <span class="metric-value" [style.color]="company.growthRateColor">
                  {{ company.growthRate }}%
                </span>
                <highcharts-chart
                  *ngIf="company.growthSparklineOptions"
                  [Highcharts]="Highcharts"
                  [options]="company.growthSparklineOptions"
                  class="inline-sparkline">
                </highcharts-chart>
              </div>
            </td>
            <td class="metric-with-trend-cell">
              <div class="metric-trend-container">
                <span class="metric-value" [style.color]="company.turnoverRateColor">
                  {{ company.turnoverRate }}%
                </span>
                <highcharts-chart
                  *ngIf="company.turnoverSparklineOptions"
                  [Highcharts]="Highcharts"
                  [options]="company.turnoverSparklineOptions"
                  class="inline-sparkline">
                </highcharts-chart>
              </div>
            </td>
            <td class="metric-value" [style.color]="company.execTurnoverRateColor">
              {{ company.execTurnoverRate }}%
            </td>
            <td class="metric-value" [style.color]="company.departedTenureColor">
              {{ company.departedTenure }} yrs
            </td>
          </tr>

          <!-- Expanded Detail Row -->
          <tr *ngIf="selectedCompany === company.company" class="expanded-detail-row">
            <td [attr.colspan]="isJobFunctionFilterActive ? 8 : 11">
              <div class="company-comparison-inline">
                <!-- Comparison Charts Row -->
                <div class="charts-grid-2col">
                  <!-- Radar Chart -->
                  <div class="chart-card">
                    <highcharts-chart
                      [Highcharts]="Highcharts"
                      [options]="radarChartOptions"
                      [(update)]="radarUpdateFlag"
                      style="width: 100%; display: block;">
                    </highcharts-chart>
                  </div>

                  <!-- Metric Comparison Bars -->
                  <div class="chart-card">
                    <h3 class="chart-title">Metric Comparison</h3>
                    <p class="chart-subtitle">
                      <span class="company-legend">■</span> {{ company.company }} vs
                      <span class="sector-legend">■</span> Average
                    </p>
                    <div class="metrics-comparison-container">
                      <div *ngFor="let metric of comparisonMetrics" class="metric-comparison-item">
                        <div class="metric-header">
                          <span class="metric-name">{{ metric.metric }}</span>
                          <span class="metric-values" [style.color]="metric.companyValueColor">
                            {{ metric.company }}{{ metric.unit }} <span class="vs-small">vs</span>
                            {{ metric.sector }}{{ metric.unit }}
                          </span>
                        </div>
                        <div class="metric-bars">
                          <div class="metric-bar-container">
                            <div
                              class="metric-bar company-bar"
                              [style.width.%]="metric.companyBarWidth"
                              [style.background]="metric.companyBarColor"></div>
                          </div>
                        </div>
                        <div class="metric-bars sector-bars">
                          <div class="metric-bar-container sector-bar-container">
                            <div
                              class="metric-bar sector-bar"
                              [style.width.%]="metric.sectorBarWidth"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
  </div>

  <!-- Metric Definitions -->
  <div class="metric-definitions" *ngIf="!isLoading">
    <div class="definition-card">
      <h3 class="definition-title">📊 Point-in-Time (As of {{ dateTo | date: 'MMM yyyy' }})</h3>
      <p class="definition-text">
        <strong>Sample Size:</strong> Professionals tracked* at latest date<br />
        <strong>Eng %:</strong> Engineering % of total headcount<br />
        <strong>Sales %:</strong> Sales % of total headcount<br />
        <strong>Eng:Sales:</strong> Engineering to sales ratio<br />
        <strong>Avg Tenure:</strong> Avg tenure of current employees
      </p>
    </div>
    <div class="definition-card">
      <h3 class="definition-title">⚠️ Over Time ({{ dateRangeLabel }})</h3>
      <p class="definition-text">
        <strong>Growth %:</strong> Net headcount change over period<br />
        <strong>Turnover %:</strong> Departures / avg headcount over period<br />
        <strong>Exec Turn %:</strong> C-Team/VP departures over period<br />
        <strong>Departed Tenure:</strong> Avg tenure of departed employees
      </p>
    </div>
    <div class="definition-card">
      <h3 class="definition-title">✅ Health Score</h3>
      <p class="definition-text">
        Composite score (0-100) based on growth, turnover, exec stability, and retention.
        <span class="healthy-text">Green</span> = above sector avg,
        <span class="concern-text">Red</span> = below.
      </p>
    </div>
  </div>

  <!-- Footer -->
  <div class="dashboard-footer" *ngIf="!isLoading">
    <p class="footer-note">
      <strong>* Sample Size Note:</strong> Figures represent professionals tracked in the
      workforce.ai database with verified employment records—a statistically significant sample for
      trend analysis, not official company headcount. Actual total employee counts may vary.
    </p>
  </div>
</div>
